import { Skeleton } from '@dnb/eufemia';
import classnames from 'classnames';
import { type JSX, type PropsWithChildren, type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import useReturnTo from '@/hooks/useReturnTo';

import BaseLayout from '../BaseLayout';
import HeroHeader from './HeroHeader';

import style from './index.module.css';

import ExplorerIllustration from '@/illustrations/Explorer.svg?url';
import EmeraldMint1 from '@/illustrations/supergraphics/emerald_mint_1.avif?url';
import OceanSummer2 from '@/illustrations/supergraphics/ocean_summer_2.avif?url';
import SummerEmerald1 from '@/illustrations/supergraphics/summer_emerald_1.avif?url';

/**
 * - common: For landing page and common documentation pages.
 * - explorer: For explorer pages.
 * - personal: For user, team, app & api pages.
 * - documentation: For individual api/event documentation pages.
 */
const banners = {
  common: {
    backgroundImage: OceanSummer2,
  },
  explorer: {
    backgroundImage: SummerEmerald1,
  },
  personal: {
    backgroundImage: EmeraldMint1,
  },
  documentation: {
    backgroundColor: 'var(--color-ocean-green)',
    illustration: ExplorerIllustration,
  },
};

type PageProps = {
  title: string | undefined;
  subtitle?: string | ReactNode;
  banner: keyof typeof banners;
  heroChildren?: ReactNode;
  skeleton?: boolean;
  noTopMargin?: boolean;
  heroAltText?: string;
  heroLeftComponent?: ReactNode;
  wideHeader?: boolean;
  tabTitle?: string;
};

/**
 * @param title Should always have a string, but can be undefined as we sometimes
 * wait for the title from backend. if it is undefined and skeleton=true then
 * skeleton will show up instead.
 * @param subtitle Can be a string or a ReactNode, allowing for more complex subtitle content.
 *
 * @param banner Determines the background and illustration of the header.
 *
 * @param heroChildren
 * @param children *NB*: Passing multiple children will cause them to overlap in
 * the grid unless they are specifically placed in different grid areas.
 * Using e.g. <HeroMenu/> or <HeroTopContent/> will place items in different areas.
 *
 * @param skeleton
 * @param noTopMargin
 * @param heroLeftComponent
 * @param wideHeader option to increase header width to be wider than default page width.
 * @param tabTitle
 *
 * @example
 * <HeroPage>
 *  <HeroTopContent>{topContent}</HeroTopContent>
 *  // {menuContent} is only shown when screen width is larger than 82rem
 *  <HeroMenu>{menuContent}</HeroMenu>
 *  <HeroContent>
 *    // {contentMenuContent} is only shown when screen width is smaller than 82 rem
 *    <HeroContentMenu>{contentMenuContent}</HeroContentMenu>
 *    {pageContent}
 *  </HeroContent>
 * </HeroPage>
 */
export default function HeroPage({
  title,
  subtitle,
  banner,
  heroChildren,
  children,
  skeleton = false,
  noTopMargin = false,
  heroLeftComponent,
  wideHeader = false,
  tabTitle,
}: PropsWithChildren<PageProps>): JSX.Element {
  const location = useLocation();
  const setReturnTo = useReturnTo()[1];

  useEffect(() => {
    const newTitle = tabTitle ?? title;
    if (newTitle) document.title = newTitle;
  }, [tabTitle, title]);

  useEffect(() => {
    setReturnTo(location.pathname);
  }, [location.pathname, setReturnTo]);

  return (
    <BaseLayout>
      <header>
        <HeroHeader
          heroLeftComponent={heroLeftComponent}
          heroSubtitle={subtitle}
          heroText={title}
          skeleton={skeleton}
          wideHeader={wideHeader}
          {...banners[banner]}
        >
          {heroChildren && heroChildren}
        </HeroHeader>
      </header>

      <Skeleton
        className={classnames({
          [style['Content']!]: true,
          [style['TopMargin']!]: !noTopMargin,
        })}
        show={skeleton}
      >
        {children}
      </Skeleton>
    </BaseLayout>
  );
}
